import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { 
  Plus, 
  Trash2, 
  Edit,
  X,
  Check,
  Video,
  Play,
  Youtube,
  Instagram,
  ExternalLink,
  Eye,
  EyeOff,
  Upload,
  Loader2,
  Smartphone
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// TikTok icon
const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

const VIDEO_TYPES = [
  { id: 'upload', name: 'Upload din Telefon', icon: Smartphone, color: 'text-green-500' },
  { id: 'youtube', name: 'YouTube', icon: Youtube, color: 'text-red-500' },
  { id: 'tiktok', name: 'TikTok', icon: TikTokIcon, color: 'text-white' },
  { id: 'instagram', name: 'Instagram', icon: Instagram, color: 'text-pink-500' },
  { id: 'link', name: 'Link Direct', icon: Video, color: 'text-sky-500' }
];

const CATEGORIES = [
  { id: 'testimonial', name: 'Testimonial Client', emoji: '⭐' },
  { id: 'product', name: 'Prezentare Produs', emoji: '📦' },
  { id: 'story', name: 'Povestea Mea', emoji: '💝' }
];

const DISPLAY_OPTIONS = [
  { id: 'homepage', name: 'Pagina Principală' },
  { id: 'gallery', name: 'Galerie Video' },
  { id: 'mystory', name: 'Povestea Mea' }
];

const AdminVideos = () => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingVideo, setEditingVideo] = useState(null);
  const [message, setMessage] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    video_type: 'upload',
    video_url: '',
    thumbnail_url: '',
    category: 'testimonial',
    display_on: ['gallery']
  });

  const fetchVideos = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/videos`);
      setVideos(response.data.videos || []);
    } catch (error) {
      console.error('Error fetching videos:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVideos();
  }, []);

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['video/mp4', 'video/quicktime', 'video/x-msvideo', 'video/webm', 'video/mpeg', 'video/mov'];
    if (!allowedTypes.includes(file.type) && !file.name.toLowerCase().endsWith('.mov')) {
      setMessage({ type: 'error', text: 'Format invalid. Folosește MP4, MOV, AVI sau WebM.' });
      return;
    }

    // Check file size (100MB max)
    if (file.size > 100 * 1024 * 1024) {
      setMessage({ type: 'error', text: 'Fișierul este prea mare. Maxim 100MB.' });
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setMessage(null);

    const uploadFormData = new FormData();
    uploadFormData.append('file', file);

    try {
      const response = await axios.post(`${API_URL}/api/admin/upload/video`, uploadFormData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        }
      });

      if (response.data.success) {
        // Build full URL if relative path returned
        let videoUrl = response.data.url;
        if (videoUrl.startsWith('/')) {
          videoUrl = `${API_URL}${videoUrl}`;
        }
        setFormData(prev => ({
          ...prev,
          video_url: videoUrl,
          video_type: 'upload'
        }));
        setMessage({ type: 'success', text: 'Video încărcat cu succes!' });
      }
    } catch (error) {
      console.error('Upload error:', error);
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Eroare la încărcarea video-ului.' });
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage(null);

    if (!formData.video_url) {
      setMessage({ type: 'error', text: 'Te rog încarcă sau adaugă un link video.' });
      return;
    }

    try {
      if (editingVideo) {
        await axios.put(`${API_URL}/api/admin/videos/${editingVideo.id}`, formData);
        setMessage({ type: 'success', text: 'Video actualizat!' });
      } else {
        await axios.post(`${API_URL}/api/admin/videos`, formData);
        setMessage({ type: 'success', text: 'Video adăugat!' });
      }
      
      resetForm();
      fetchVideos();
    } catch (error) {
      setMessage({ type: 'error', text: 'Eroare la salvarea video-ului.' });
    }
  };

  const handleEdit = (video) => {
    setEditingVideo(video);
    setFormData({
      title: video.title,
      description: video.description || '',
      video_type: video.video_type,
      video_url: video.video_url,
      thumbnail_url: video.thumbnail_url || '',
      category: video.category,
      display_on: video.display_on || ['gallery']
    });
    setShowForm(true);
  };

  const handleDelete = async (videoId) => {
    if (!window.confirm('Sigur vrei să ștergi acest video?')) return;

    try {
      await axios.delete(`${API_URL}/api/admin/videos/${videoId}`);
      setMessage({ type: 'success', text: 'Video șters!' });
      fetchVideos();
    } catch (error) {
      setMessage({ type: 'error', text: 'Eroare la ștergerea video-ului.' });
    }
  };

  const toggleActive = async (video) => {
    try {
      await axios.put(`${API_URL}/api/admin/videos/${video.id}`, {
        is_active: !video.is_active
      });
      fetchVideos();
    } catch (error) {
      console.error('Error toggling video status:', error);
    }
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingVideo(null);
    setFormData({
      title: '',
      description: '',
      video_type: 'upload',
      video_url: '',
      thumbnail_url: '',
      category: 'testimonial',
      display_on: ['gallery']
    });
  };

  const toggleDisplayOn = (option) => {
    setFormData(prev => ({
      ...prev,
      display_on: prev.display_on.includes(option)
        ? prev.display_on.filter(o => o !== option)
        : [...prev.display_on, option]
    }));
  };

  const getVideoTypeIcon = (type) => {
    const found = VIDEO_TYPES.find(t => t.id === type);
    if (found) {
      const Icon = found.icon;
      return <Icon className={`w-4 h-4 ${found.color}`} />;
    }
    return <Video className="w-4 h-4" />;
  };

  const getThumbnail = (video) => {
    if (video.thumbnail_url) return video.thumbnail_url;
    if (video.video_type === 'youtube') {
      const match = video.video_url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\s]+)/);
      if (match) return `https://img.youtube.com/vi/${match[1]}/mqdefault.jpg`;
    }
    return null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-sky-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-videos-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white" data-testid="admin-videos-title">Video-uri</h1>
          <p className="text-slate-400 mt-1" data-testid="admin-videos-count">{videos.length} video-uri în galerie</p>
        </div>
        <Button
          onClick={() => setShowForm(true)}
          className="bg-sky-500 hover:bg-sky-600 text-white rounded-xl"
          data-testid="admin-add-video-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Adaugă Video
        </Button>
      </div>

      {/* Message */}
      {message && (
        <div className={`p-4 rounded-xl flex items-center gap-2 ${
          message.type === 'success' 
            ? 'bg-green-500/20 text-green-400 border border-green-500/50' 
            : 'bg-red-500/20 text-red-400 border border-red-500/50'
        }`}>
          {message.type === 'success' ? <Check className="w-5 h-5" /> : <X className="w-5 h-5" />}
          {message.text}
        </div>
      )}

      {/* Add/Edit Form */}
      {showForm && (
        <div className="bg-slate-800 rounded-2xl p-6 border border-slate-700" data-testid="admin-video-form">
          <h2 className="text-xl font-bold text-white mb-4" data-testid="admin-video-form-title">
            {editingVideo ? 'Editează Video' : 'Video Nou'}
          </h2>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="admin-video-form-content">
            
            {/* Upload Section - Prominent */}
            <div className="bg-gradient-to-br from-green-500/20 to-emerald-500/20 border-2 border-dashed border-green-500/50 rounded-2xl p-6">
              <div className="text-center">
                <Smartphone className="w-12 h-12 text-green-400 mx-auto mb-3" />
                <h3 className="text-lg font-bold text-white mb-2">📱 Încarcă din Telefon</h3>
                <p className="text-slate-400 text-sm mb-4">
                  Selectează un video din galeria telefonului tău (MP4, MOV - max 100MB)
                </p>
                
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="video/mp4,video/quicktime,video/x-msvideo,video/webm,.mp4,.mov,.avi,.webm"
                  onChange={handleFileUpload}
                  className="hidden"
                  data-testid="admin-video-file-input"
                />
                
                <Button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="bg-green-500 hover:bg-green-600 text-white rounded-xl px-6"
                  data-testid="admin-video-upload-btn"
                >
                  {uploading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Se încarcă... {uploadProgress}%
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Alege Video
                    </>
                  )}
                </Button>
                
                {uploading && (
                  <div className="mt-4 w-full bg-slate-700 rounded-full h-2" data-testid="admin-video-upload-progress-container">
                    <div 
                      className="bg-green-500 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                      data-testid="admin-video-upload-progress"
                    />
                  </div>
                )}
                
                {formData.video_url && formData.video_type === 'upload' && (
                  <div className="mt-4 p-3 bg-green-500/20 rounded-xl" data-testid="admin-video-upload-success">
                    <p className="text-green-400 text-sm">✅ Video încărcat cu succes!</p>
                  </div>
                )}
              </div>
            </div>

            {/* OR Divider */}
            <div className="flex items-center gap-4">
              <div className="flex-1 border-t border-slate-600"></div>
              <span className="text-slate-500 text-sm">SAU adaugă link</span>
              <div className="flex-1 border-t border-slate-600"></div>
            </div>

            {/* Video Type Selection for Links */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Tip Link Video</label>
              <div className="flex flex-wrap gap-2">
                {VIDEO_TYPES.filter(t => t.id !== 'upload').map(type => (
                  <button
                    key={type.id}
                    type="button"
                    onClick={() => setFormData({...formData, video_type: type.id})}
                    className={`px-4 py-2 rounded-xl flex items-center gap-2 transition-colors ${
                      formData.video_type === type.id && formData.video_type !== 'upload'
                        ? 'bg-sky-500 text-white'
                        : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                    }`}
                  >
                    <type.icon className="w-4 h-4" />
                    {type.name}
                  </button>
                ))}
              </div>
            </div>

            {/* URL Input for Links */}
            {formData.video_type !== 'upload' && (
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">
                  URL Video
                  <span className="text-slate-500 text-xs ml-2">
                    {formData.video_type === 'youtube' && '(Link YouTube)'}
                    {formData.video_type === 'tiktok' && '(Link TikTok)'}
                    {formData.video_type === 'instagram' && '(Link Instagram Reel)'}
                    {formData.video_type === 'link' && '(Link direct către video)'}
                  </span>
                </label>
                <input
                  type="url"
                  value={formData.video_url}
                  onChange={(e) => setFormData({...formData, video_url: e.target.value})}
                  placeholder="https://..."
                  className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-sky-500/50"
                />
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">Titlu *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  required
                  placeholder="ex: Testimonial Maria - X39"
                  className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-sky-500/50"
                  data-testid="admin-video-title-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">Categorie *</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({...formData, category: e.target.value})}
                  className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-sky-500/50"
                  data-testid="admin-video-category-select"
                >
                  {CATEGORIES.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.emoji} {cat.name}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">Descriere (opțional)</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                rows={2}
                placeholder="Scurtă descriere..."
                className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-sky-500/50 resize-none"
              />
            </div>

            {/* Display On Selection */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Afișează pe *</label>
              <div className="flex flex-wrap gap-2">
                {DISPLAY_OPTIONS.map(option => (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => toggleDisplayOn(option.id)}
                    className={`px-4 py-2 rounded-xl transition-colors ${
                      formData.display_on.includes(option.id)
                        ? 'bg-green-500 text-white'
                        : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                    }`}
                  >
                    {formData.display_on.includes(option.id) && <Check className="w-4 h-4 inline mr-1" />}
                    {option.name}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <Button type="submit" className="bg-sky-500 hover:bg-sky-600 text-white rounded-xl" data-testid="admin-video-submit-btn">
                {editingVideo ? 'Salvează Modificările' : 'Adaugă Video'}
              </Button>
              <Button type="button" variant="outline" onClick={resetForm} className="rounded-xl border-slate-600 text-slate-300 hover:bg-slate-700" data-testid="admin-video-cancel-btn">
                Anulează
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* Videos Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="admin-videos-grid">
        {videos.map((video) => (
          <div 
            key={video.id} 
            className={`bg-slate-800 rounded-2xl border overflow-hidden group ${
              video.is_active ? 'border-slate-700' : 'border-red-500/50 opacity-60'
            }`}
            data-testid={`admin-video-card-${video.id}`}
          >
            {/* Thumbnail */}
            <div className="aspect-video bg-slate-700 relative">
              {getThumbnail(video) ? (
                <img 
                  src={getThumbnail(video)} 
                  alt={video.title}
                  className="w-full h-full object-cover"
                />
              ) : video.video_type === 'upload' ? (
                <video 
                  src={video.video_url} 
                  className="w-full h-full object-cover"
                  preload="metadata"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Video className="w-12 h-12 text-slate-600" />
                </div>
              )}
              
              {/* Play overlay */}
              <a 
                href={video.video_url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
              >
                <div className="w-16 h-16 bg-white/20 backdrop-blur rounded-full flex items-center justify-center">
                  <Play className="w-8 h-8 text-white fill-white" />
                </div>
              </a>

              {/* Type badge */}
              <div className="absolute top-2 left-2 px-2 py-1 bg-black/70 rounded-full flex items-center gap-1">
                {getVideoTypeIcon(video.video_type)}
                <span className="text-white text-xs">
                  {video.video_type === 'upload' ? 'Uploadat' : VIDEO_TYPES.find(t => t.id === video.video_type)?.name}
                </span>
              </div>

              {/* Category badge */}
              <div className="absolute top-2 right-2 px-2 py-1 bg-black/70 rounded-full">
                <span className="text-xs">
                  {CATEGORIES.find(c => c.id === video.category)?.emoji}
                </span>
              </div>
            </div>

            {/* Info */}
            <div className="p-4">
              <h3 className="font-bold text-white mb-1 truncate">{video.title}</h3>
              {video.description && (
                <p className="text-slate-400 text-sm line-clamp-2 mb-2">{video.description}</p>
              )}
              
              {/* Display locations */}
              <div className="flex flex-wrap gap-1 mb-3">
                {video.display_on?.map(loc => (
                  <span key={loc} className="px-2 py-0.5 bg-slate-700 text-slate-300 text-xs rounded-full">
                    {DISPLAY_OPTIONS.find(o => o.id === loc)?.name}
                  </span>
                ))}
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2">
                <Button
                  onClick={() => toggleActive(video)}
                  variant="ghost"
                  size="sm"
                  className={video.is_active ? 'text-green-400 hover:bg-green-500/20' : 'text-red-400 hover:bg-red-500/20'}
                  data-testid={`admin-video-toggle-${video.id}`}
                >
                  {video.is_active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                </Button>
                <Button
                  onClick={() => handleEdit(video)}
                  variant="ghost"
                  size="sm"
                  className="text-sky-400 hover:bg-sky-500/20"
                  data-testid={`admin-video-edit-${video.id}`}
                >
                  <Edit className="w-4 h-4" />
                </Button>
                <Button
                  onClick={() => handleDelete(video.id)}
                  variant="ghost"
                  size="sm"
                  className="text-red-400 hover:bg-red-500/20"
                  data-testid={`admin-video-delete-${video.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
                <a
                  href={video.video_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ml-auto text-slate-400 hover:text-white"
                >
                  <ExternalLink className="w-4 h-4" />
                </a>
              </div>
            </div>
          </div>
        ))}
      </div>

      {videos.length === 0 && !loading && (
        <div className="text-center py-12 bg-slate-800 rounded-2xl border border-slate-700" data-testid="admin-videos-empty">
          <Video className="w-12 h-12 text-slate-600 mx-auto mb-4" />
          <p className="text-slate-400">Nu ai video-uri încă</p>
          <p className="text-slate-500 text-sm mt-1">Încarcă primul video din telefon sau adaugă un link</p>
        </div>
      )}

      {/* Info */}
      <div className="p-4 bg-sky-500/10 border border-sky-500/30 rounded-xl">
        <p className="text-sky-300 text-sm">
          <strong>💡 Sfat:</strong> Poți încărca video-uri direct din telefonul tău (max 100MB) sau adăuga link-uri de pe YouTube, TikTok sau Instagram!
        </p>
      </div>
    </div>
  );
};

export default AdminVideos;
